import React, { useEffect, useRef, useState } from "react";
import { Bar } from "react-chartjs-2";
import { useDispatch } from "react-redux";
import { getNonRenewedSubscription } from "../../../services/subscriptionService";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
// Loader removed from this widget per request

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
);

const NonRenewedSubscriptions = ({ selectedFrequency }) => {
  const dispatch = useDispatch();
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        grid: {
          display: false,
        },
        border: {
          display: false,
        },
        stacked: false,
        ticks: {
          padding: 10,
          display: true,
        },
        afterTickToLabelConversion: function (scaleInstance) {
          const ticks = scaleInstance.ticks;
          const ticksPostion = scaleInstance.chart.scales.x.right;
          scaleInstance.chart.ctx.save();
          scaleInstance.chart.ctx.beginPath();
          ticks.forEach(function (tick) {
            scaleInstance.chart.ctx.moveTo(tick.x, ticksPostion);
            scaleInstance.chart.ctx.lineTo(tick.x, ticksPostion + 8);
            scaleInstance.chart.ctx.strokeStyle = "#E5E7EB";
            scaleInstance.chart.ctx.stroke();
          });
          scaleInstance.chart.ctx.restore();
        },
      },
      y: {
        beginAtZero: true,
        grid: {
          color: "#E5E7EB",
        },
        border: {
          display: false,
        },
        stacked: false,
        ticks: {
          stepSize: 10,
          callback: function (value) {
            return value;
          },
        },
      },
    },
    plugins: {
      legend: {
        position: "bottom",
        labels: {
          boxWidth: 50,
          boxHeight: 14,
          usePointStyle: true,
          pointStyle: "rectRounded",
          padding: 20,
        },
      },
      tickMarks: {
        enabled: true,
        tickLength: 8,
        tickColor: "#E5E7EB",
      },
    },
    barThickness: 8,
  };
  const buildEmptyChartData = () => {
    const datasets = [
      {
        label: "Total",
        data: new Array(12).fill(0),
        backgroundColor: "#B154C6",
        borderRadius: 0,
      },
      {
        label: "Cancelled",
        data: new Array(12).fill(0),
        backgroundColor: "#F59E0B",
        borderRadius: 0,
      },
      // Note: 'Non-Renewed Subscriptions' represents subscriptions with status = 'expired'
      {
        label: "Non-Renewed Subscriptions",
        data: new Array(12).fill(0),
        backgroundColor: "#14DFB9",
        borderRadius: 0,
      },
      {
        label: "Payment Failed",
        data: new Array(12).fill(0),
        backgroundColor: "#EF4444",
        borderRadius: 0,
      },
    ];

    return {
      labels: [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ],
      datasets,
      isEmpty: true,
      options: {
        ...options,
        scales: {
          ...options.scales,
          y: { ...options.scales.y, max: 10 },
        },
      },
    };
  };
  const [chartData, setChartData] = useState(buildEmptyChartData);
  const inFlightRef = useRef(false);
  const getMaxValue = (datasets = []) => {
    const values = datasets.flatMap((dataset) => dataset?.data || []);
    const highest = Math.max(0, ...values);
    return Math.max(10, Math.ceil(highest / 10) * 10);
  };

  useEffect(() => {
    const fetchData = async () => {
      if (inFlightRef.current) return;
      inFlightRef.current = true;
      try {
        const tfInput =
          selectedFrequency ||
          (typeof window !== "undefined" &&
            window.__ADMIN_SELECTED_FREQUENCY__) ||
          "Monthly";
        const tf = String(tfInput || "").toLowerCase();
        const map = {
          today: "today",
          weekly: "week",
          monthly: "month",
          yearly: "year",
        };
        const timeframe = map[tf] || tf || "month";

        const res = await dispatch(
          getNonRenewedSubscription({ timeframe }),
        )?.unwrap();
        if (!res?.success) {
          setChartData(buildEmptyChartData());
          return;
        }

        const root = res?.stats || res?.data || {};
        const data =
          root?.data && typeof root?.data === "object" ? root.data : root;

        let d = data;
        if (!Array.isArray(d) && d && typeof d === "object") {
          d =
            data?.monthly ||
            data?.month ||
            data?.weekly ||
            data?.week ||
            data?.yearly ||
            data?.year ||
            data?.today ||
            d;
        }

        if (Array.isArray(d)) {
          const labels = d.map(
            (m) => m?.month ?? m?.label ?? m?.day ?? m?.year ?? "",
          );
          const total = d.map(
            (m) => Number(m?.total ?? m?.subscriptions ?? m?.count) || 0,
          );
          const cancelled = d.map(
            (m) => Number(m?.cancelled ?? m?.cancelledCount) || 0,
          );
          const expired = d.map(
            (m) => Number(m?.expired ?? m?.expiredCount) || 0,
          );
          const paymentFailed = d.map(
            (m) => Number(m?.paymentFailed ?? m?.paymentFailedCount) || 0,
          );
          d = { labels, total, cancelled, expired, paymentFailed };
        }

        const datasets = [
          {
            label: "Total",
            data: d?.total || d?.totals || [],
            backgroundColor: "#B154C6",
            borderRadius: 0,
          },
          {
            label: "Cancelled",
            data: d?.cancelled || [],
            backgroundColor: "#F59E0B",
            borderRadius: 0,
          },
          // Note: 'Non-Renewed Subscriptions' represents subscriptions with status = 'expired'
          {
            label: "Non-Renewed Subscriptions",
            data: d?.expired || [],
            backgroundColor: "#14DFB9",
            borderRadius: 0,
          },
          {
            label: "Payment Failed",
            data: d?.paymentFailed || [],
            backgroundColor: "#EF4444",
            borderRadius: 0,
          },
        ];
        const hasData = datasets.some((dataset) =>
          (dataset?.data || []).some((value) => Number(value) > 0),
        );
        const normalizedChart = {
          labels: d?.labels || [
            "Jan",
            "Feb",
            "Mar",
            "Apr",
            "May",
            "Jun",
            "Jul",
            "Aug",
            "Sep",
            "Oct",
            "Nov",
            "Dec",
          ],
          datasets,
          isEmpty: !hasData,
        };
        normalizedChart.options = {
          ...options,
          scales: {
            ...options.scales,
            y: { ...options.scales.y, max: getMaxValue(datasets) },
          },
        };
        setChartData(normalizedChart);
      } catch (_) {
        setChartData(buildEmptyChartData());
      } finally {
        inFlightRef.current = false;
      }
    };
    fetchData();
  }, [dispatch, selectedFrequency]);

  return (
    <div className="summary w-full p-4 h-[470px] flex flex-col bg-white rounded-lg">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-[20px] font-poppins font-[600] text-[#303030] leading-none">
          Non-Renewed Subscriptions
        </h2>
      </div>
      <div className="flex-1 min-h-0 relative">
        <Bar options={chartData.options || options} data={chartData} />
        {chartData?.isEmpty && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none px-6">
            <div className="text-center bg-white/85 px-5 py-3 rounded-[12px]">
              <p className="text-[#303030] text-[15px] font-poppins font-[600] leading-[20px]">
                No data available
              </p>
              <p className="text-[#8A8A8A] text-[12px] font-poppins leading-[18px] mt-1">
                No non-renewed subscriptions in this period.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default NonRenewedSubscriptions;
